/**
 * @file useWarehousePolicies.ts
 * @description Состояние и сохранение правил склада (phase19.31, зона
 * «Склад»: вынесено из `WarehousePoliciesPanel`, чтобы панель осталась
 * раскладкой, а «Сохранить» встал в верхнюю панель страницы).
 *
 * Значения приходят из `warehouse.settings`; после сохранения документ
 * перечитывается с сервера и отдаётся наверх через `onUpdated` (один источник
 * правды). «Сохранить» неактивна, пока ничего не изменено (`dirty`).
 * Ответ 400 сервера — под полем порога черновика, остальное — всплывающим
 * сообщением человеческим текстом.
 */

import { useState } from "react";

import { STALE_DRAFT_DAYS } from "./StaleDraftDaysField";
import { useNotify } from "../../ui/NotifyProvider";
import { ApiError } from "../../../lib/api-client";
import { warehousesApi, type WarehouseDto } from "../../../lib/api-wms";
import { validateQty } from "../../../lib/qty-input";

/** Правила склада в форме. Порог черновика — строкой: поле можно очистить. */
export interface PoliciesDraft {
  defaultFefo: boolean;
  crptRequired: boolean;
  allowNegativeStock: boolean;
  requireBinScanOnPick: boolean;
  expiryAlertDays: number;
  expiryCriticalDays: number;
  staleDraftDays: string;
}

/** Значения формы из настроек склада (умолчания — как на сервере). */
export function policiesFromWarehouse(warehouse: WarehouseDto): PoliciesDraft {
  const s = warehouse.settings;
  return {
    defaultFefo: s?.defaultFefo ?? true,
    crptRequired: s?.crptRequired ?? false,
    // phase19.13 шаг 8 — по умолчанию разрешено (склад ещё не выверен).
    allowNegativeStock: s?.allowNegativeStock ?? true,
    requireBinScanOnPick: s?.requireBinScanOnPick ?? false,
    expiryAlertDays: s?.expiryAlertDays ?? 30,
    expiryCriticalDays: s?.expiryCriticalDays ?? 7,
    staleDraftDays: String(s?.staleDraftDays ?? STALE_DRAFT_DAYS.default),
  };
}

export function useWarehousePolicies(warehouse: WarehouseDto, onUpdated: (fresh: WarehouseDto) => void) {
  const notify = useNotify();
  // Сохранённые значения: после «Сохранить» становятся текущими — кнопка гаснет.
  const [initial, setInitial] = useState(() => policiesFromWarehouse(warehouse));
  const [draft, setDraft] = useState<PoliciesDraft>(initial);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Границы ловим до PATCH: реестр `qty-input` даёт и шаг поля, и текст ошибки.
  const staleInputError = validateQty(draft.staleDraftDays, STALE_DRAFT_DAYS.unit, {
    min: STALE_DRAFT_DAYS.min,
    max: STALE_DRAFT_DAYS.max,
  });
  const dirty = (Object.keys(initial) as (keyof PoliciesDraft)[]).some((key) => initial[key] !== draft[key]);

  /** Точечная правка поля; ответ сервера под полем порога сбрасываем. */
  function patch(part: Partial<PoliciesDraft>) {
    setDraft((prev) => ({ ...prev, ...part }));
    if ("staleDraftDays" in part) setServerError(null);
  }

  async function save() {
    if (staleInputError) return;
    setSaving(true);
    try {
      await warehousesApi.updateSettings(warehouse.id, {
        defaultFefo: draft.defaultFefo,
        crptRequired: draft.crptRequired,
        expiryAlertDays: draft.expiryAlertDays,
        expiryCriticalDays: draft.expiryCriticalDays,
        allowNegativeStock: draft.allowNegativeStock,
        requireBinScanOnPick: draft.requireBinScanOnPick,
        staleDraftDays: Number(draft.staleDraftDays),
      });
      setInitial(draft);
      onUpdated(await warehousesApi.findOne(warehouse.id));
      notify.success("Правила склада сохранены");
    } catch (err) {
      // 400 — это проверка полей формы: текст под полем, а не улетающий тост.
      if (err instanceof ApiError && err.status === 400) setServerError(err.message);
      else notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить правила склада. Попробуйте ещё раз.");
    } finally {
      setSaving(false);
    }
  }

  return {
    draft,
    patch,
    dirty,
    saving,
    staleError: serverError ?? staleInputError,
    canSave: dirty && !staleInputError && !saving,
    save,
  };
}
